import { find, get, includes, some } from 'lodash';
import { sendLog, LoggerStatusEnum } from 'ui-model';

import api from '@lib/utils/axiosClient';
import { usersResource } from '@lib/resources';

import { isMaster } from 'utils/permissions';
import { isCHC } from 'utils/constants';

const getInvalidMessage = () => {
  let invalidMessage = '';
  if (isCHC) {
    invalidMessage = 'Invalid username or password, please try again.';
    return invalidMessage;
  }
  invalidMessage = `Invalid username or password, please try again. Need help? Email support@lumahealth.io or call (415) 741–3377.`;
  return invalidMessage;
};

// Methods
async function signIn({ credentials }) {
  try {
    const res = await api.post('/users/login', credentials, {
      schema: usersResource.schema,
    });
    const user = res.data;
    const { data } = await api.get(`/settings/${user.setting}`, {
      params: { token: user.token },
    });
    const userSettings = data.settings;

    const provisioning = get(user, 'salesforceData.provisioning');

    // if it has only recall and upload
    if (provisioning.length === 2 && includes(provisioning, 'recall') && includes(provisioning, 'upload')) {
      window.location.href = `/patients?token=${user.token}`;
      return;
    }
    // if it has only one of the provisionings with no access to activity feed, logs in the patients screen
    // TODO use `hasAccessToProduct`
    if (provisioning.length === 1) {
      if (some(['followup', 'upload', 'branding', 'insurance', 'recall'], (item) => includes(provisioning, item))) {
        window.location.href = `/patients?token=${user.token}`;
        return;
      }
    }
    // if they only have telahealth provisioned logs in the schedule screen (providers).
    const hasOnlyTelehealth = includes(provisioning, 'telehealth') && provisioning.length === 1;
    if (hasOnlyTelehealth) {
      window.location.href = `/schedule?token=${user.token}`;
      return;
    }
    // if has only chat logs in the hub screen.
    const hasOnlyChat = includes(provisioning, 'chat') && provisioning.length === 1;
    if (hasOnlyChat) {
      window.location.href = `/hub?token=${user.token}`;
      return;
    }

    if (!userSettings.welcome.termsAgreementDate && !user.settings.welcome.termsAgreementDate) {
      // signup from salesforce
      if (user.salesforceId) {
        window.location.href = `/signup/${user._id}/${user.token}`;
      }
      // signup from group invite
      if (!isMaster(user)) {
        const invites = await api.get(`/users/${user._id}/groupInvites`, {
          params: { token: user.token },
        });
        const invite = find(invites, { email: user.email });
        window.location.href = `join/${invite.token}/${user.token}`;
      }
      throw new Error('You should, first, finish your registration');
    }
    return res;
  } catch (err) {
    const error = get(err, 'response.data', {});
    // ignore error and show 2FA instead
    if (error.statusCode === 3) {
      error.dismissSnackbar = true;
      error.show2FA = true;
      sendLog('Error on authResource', error, LoggerStatusEnum.error);
      throw error;
    }
    // password expired
    if (error.code === 403) {
      error.passwordExpired = true;
      sendLog('403 in authResource', error, LoggerStatusEnum.info);
      throw error;
    }
    const invalidMessage = getInvalidMessage();
    // customize error message
    if (error.code === 401 && error.statusCode === 2) {
      error.message = /password/i.test(error.message) ? invalidMessage : 'Invalid verification code';
      sendLog(error.message, error, LoggerStatusEnum.info);
      throw error;
    }
    // wrong username and password
    if (error.code === 404) {
      error.message = invalidMessage;
      sendLog(error.message, error, LoggerStatusEnum.info);
      throw error;
    }
  }
}

function signInSubaccount({ id }) {
  return api.post(`/users/admin/${id}`, null, {
    schema: usersResource.schema,
  });
}

async function getZendeskToken() {
  try {
    const { data } = await api.post('/v2/auth/zendesk/token', {});
    const { access_token } = data;
    return access_token;
  } catch (err) {
    const error = get(err, 'response.data', {});
    if (error.code === 422) {
      error.message = 'Missing email and/or name.';
    }
    throw error;
  }
}

async function zendeskSSO(user, zendeskURL = null) {
  try {
    // Zendesk Agents have lumahealth emails and should be redirected to a different route
    if (user.email.includes('@lumahealth.io')) {
      const agentSignIn = 'https://lumahealth.zendesk.com/access/normal';
      window.open(zendeskURL || agentSignIn, '_blank');
      return;
    }
    // if the email is in the format first+something@example.com
    // remove the +something so all tickets flow in to a single
    // login for the user
    if (user.email.includes('+')) {
      const plusSplit = user.email.split('+');
      const username = plusSplit[0];
      const dirtyDomain = plusSplit[1];
      const cleanDomain = dirtyDomain.split('@')[1];
      user.email = `${username}@${cleanDomain}`; // eslint-disable-line
    }
    const { data } = await api.post('/tokens/zendesk', user);
    const { token } = data;
    const redirectTo = zendeskURL || '';
    const redirect = `https://lumahealth.zendesk.com/access/jwt?jwt=${token}&return_to=${redirectTo}`;

    window.open(redirect, '_blank');
  } catch (err) {
    const error = get(err, 'response.data', {});
    if (error.code === 422) {
      error.message = 'Missing email and/or name.';
    }
    throw error;
  }
}

export default {
  signIn,
  signInSubaccount,
  zendeskSSO,
  getZendeskToken,
};
